import { BillingApprovalItem } from '@/lib/api/financial/billing-approval-api'
import { useNavigate } from 'react-router-dom'
import {
    MdAccountBalance,
    MdCheck,
    MdClose,
    MdDelete,
    MdEvent,
    MdListAlt,
    MdPictureAsPdf,
    MdCallSplit,
    MdPayments,
    MdSettings,
    MdWarningAmber,
} from 'react-icons/md'
import ApprovalCard from './approval-card'
import CompanyBadge from './company-badge'
import { formatCurrency, formatDate } from '../format'

/** Rótulos dos meios de pagamento exibidos na fila. */
const PAYMENT_METHOD_LABELS: Record<string, string> = {
    PIX: 'PIX',
    BOLETO: 'Boleto',
    CREDIT_CARD: 'Cartão de crédito',
    DEBIT_CARD: 'Cartão de débito',
    CASH: 'Dinheiro',
    BANK_TRANSFER: 'Transferência bancária',
    CHECK: 'Cheque',
    TERMINAL: 'Maquininha',
    OTHER: 'Outro',
}

/** Meios que não passam pela conta digital — aprovar não transfere nada. */
const OFFLINE_METHODS = ['CASH', 'TERMINAL', 'OTHER']

interface PayableCardProps {
    item: BillingApprovalItem
    selectable: boolean
    selected: boolean
    onToggleSelect: () => void
    canApprove: boolean
    onApprove: () => void
    onReject: () => void
    onMarkExternal: () => void
    onClearExternal: () => void
    marking: boolean
    /**
     * Tira a solicitação da fila sem decidir — para o pedido aberto por engano.
     *
     * Só chega preenchido quando quem está olhando tem `delete` na role E é o
     * solicitante ou operador financeiro. Diferente de "Rejeitar", que é decisão e fica
     * no histórico: no lote, isto solta os repasses e apaga a conta agrupada, para o
     * financeiro refazer o recorte.
     */
    onDelete?: () => void
    /** Abre a composição do lote. Só existe para solicitação agrupada. */
    onViewGroup?: () => void
    /** Abre o demonstrativo em PDF. Só existe quando o documento foi gerado. */
    onOpenStatement?: () => void
}

/** Onde se preenche PIX/dados bancários de cada tipo de destinatário. */
const RECIPIENT_EDIT_ROUTE: Record<string, string> = {
    PARTNER: '/person/partners/edit',
    PROFESSIONAL: '/person/professionals/edit',
    SUPPLIER: '/person/suppliers/edit',
}

/** Conta a pagar aguardando aprovação — a fila que movimenta dinheiro para fora. */
export default function PayableCard({
    item,
    selectable,
    selected,
    onToggleSelect,
    canApprove,
    onApprove,
    onReject,
    onMarkExternal,
    onClearExternal,
    marking,
    onDelete,
    onViewGroup,
    onOpenStatement,
}: PayableCardProps) {
    // Gravado ao enviar para aprovação: quantas formas o pagamento tem (1 = não foi
    // dividido). Vem do metadata para a fila não precisar carregar as filhas de cada
    // solicitação só para descobrir isso.
    const splitCount = Number(item.metadata?.paymentSplitCount ?? 0)

    /**
     * Ainda não venceu. Mesma regra do `isDueForPayment` da página — repetida aqui
     * porque o card não recebe a lista, só o item; se uma delas mudar, a outra tem de
     * acompanhar.
     */
    const isUpcoming = (() => {
        if (!item.dueDate) return false
        const today = new Date()
        today.setHours(23, 59, 59, 999)
        return new Date(item.dueDate) > today
    })()
    const navigate = useNavigate()
    // O repasse ao PROFISSIONAL é conta a pagar como as outras: sem ele, a linha
    // saía sem destinatário nenhum, e é justamente o nome por onde se procura.
    const recipient =
        item.supplier?.name ||
        item.partner?.name ||
        item.professional?.name ||
        null

    // Repasse gerado pelo sistema não tem solicitante humano — dizer "—" faria
    // parecer dado faltando, quando na verdade ninguém pediu: o sistema lançou.
    const requester = item.createdBy?.name ? (
        item.createdBy.name
    ) : (
        <span className="inline-flex items-center gap-1">
            <MdSettings className="text-sm" />
            {item.category === 'PARTNER_PAYOUT'
                ? 'Sistema (repasse ao parceiro)'
                : 'Sistema'}
        </span>
    )

    return (
        <ApprovalCard
            selectable={selectable}
            selected={selected}
            onToggleSelect={onToggleSelect}
            eyebrow={
                <>
                    <span className="inline-flex items-center gap-1">
                        <MdEvent className="text-sm" />
                        Vence em {formatDate(item.dueDate)}
                    </span>
                    {/* Vencimento futuro: fora da seleção em massa, e o operador
                        precisa saber por quê antes de procurar o checkbox que "não
                        marcou". Aprovar aqui é antecipar pagamento — possível, mas
                        exige o clique individual. */}
                    {isUpcoming && (
                        <span
                            className="badge badge-xs badge-warning"
                            title="Ainda não venceu: não entra no 'selecionar vencidas'. Aprovar agora antecipa o pagamento."
                        >
                            A vencer
                        </span>
                    )}
                    <span>·</span>
                    <span>Solicitada por {requester}</span>
                    <CompanyBadge company={item.company} />
                    {/* O método decide o que "Aprovar" faz: transferência pela conta
                        digital, ou só autorizar um pagamento que sai por fora. O card
                        não mostrava isso, e o aprovador escolhia no escuro — foi assim
                        que uma conta de maquininha virou TED. */}
                    {item.paymentMethod && (
                        <>
                            <span>·</span>
                            <span
                                className="inline-flex items-center gap-1"
                                title={
                                    OFFLINE_METHODS.includes(item.paymentMethod)
                                        ? 'Pago por fora da conta digital: aprovar registra a baixa, não transfere'
                                        : 'Sai pela conta digital ao aprovar'
                                }
                            >
                                <MdPayments className="text-sm" />
                                {PAYMENT_METHOD_LABELS[item.paymentMethod] ??
                                    item.paymentMethod}
                            </span>
                        </>
                    )}
                </>
            }
            title={
                <button
                    className="link link-hover text-left hover:text-secondary"
                    onClick={() => navigate(`/financial/billings/${item.id}`)}
                >
                    {item.description || `Lançamento #${item.id}`}
                </button>
            }
            subtitle={recipient}
            amount={
                <span className="text-error">-{formatCurrency(item.amount)}</span>
            }
            actions={
                <>
                    {/* Sem PIX nem dados bancários, aprovar com transferência é
                        recusado pelo provedor. O operador precisa saber ANTES de
                        clicar — e o caminho para resolver é o cadastro, então o
                        aviso leva direto lá. */}
                    {item.transferRecipient &&
                        !item.transferRecipient.canTransfer && (
                            <button
                                className="btn btn-sm btn-ghost gap-1 text-warning"
                                title="Sem chave PIX nem dados bancários no cadastro. Aprovar com transferência será recusado — clique para preencher."
                                onClick={() =>
                                    navigate(
                                        `${RECIPIENT_EDIT_ROUTE[item.transferRecipient!.kind]}/${item.transferRecipient!.id}`,
                                    )
                                }
                            >
                                <MdWarningAmber />
                                Sem dados para transferência
                            </button>
                        )}

                    {/* Pagamento dividido em formas (parte por transferência, parte
                        em dinheiro). A aprovação continua sendo UMA — o aprovador
                        clica uma vez —, mas ele precisa saber que nem todo o valor
                        sai pela conta digital antes de autorizar. */}
                    {splitCount > 1 && (
                        <span
                            className="badge badge-sm badge-outline gap-1"
                            title="O financeiro dividiu este pagamento: parte sai por transferência e parte é paga por fora. Aprovar registra as duas."
                        >
                            <MdCallSplit className="text-sm" />
                            {splitCount} formas de pagamento
                        </span>
                    )}

                    {/* Solicitação agrupada mostra um valor somado: sem ver a
                        composição, o operador aprova um número. */}
                    {onViewGroup && (
                        <button
                            className="btn btn-sm btn-ghost gap-1 text-secondary"
                            onClick={onViewGroup}
                        >
                            <MdListAlt />
                            Ver grupo
                        </button>
                    )}

                    {/* O mesmo documento que vai ao destinatário: o operador confere
                        aqui, sem sair da fila. Abre no visualizador, que também baixa. */}
                    {onOpenStatement && (
                        <button
                            className="btn btn-sm btn-ghost gap-1 text-secondary"
                            onClick={onOpenStatement}
                        >
                            <MdPictureAsPdf />
                            Demonstrativo
                        </button>
                    )}

                    {item.externalTransferVia ? (
                        <span className="flex items-center gap-1">
                            <span
                                className="badge badge-info text-white gap-1"
                                title={`Marcada por ${item.externalTransferBy?.name || 'usuário'} — aprovar não vai transferir`}
                            >
                                <MdAccountBalance className="text-sm" />
                                Já transferida via {item.externalTransferVia}
                            </span>
                            <button
                                className="btn btn-xs btn-ghost"
                                title="Desfazer marcação"
                                onClick={onClearExternal}
                                disabled={marking}
                            >
                                <MdClose />
                            </button>
                        </span>
                    ) : (
                        /* Declarar que o pagamento já saiu por outro banco encerra a
                           fila (paga e conciliada) sem movimentar nada aqui — por isso
                           não depende de ser aprovador: quem sabe que o financeiro já
                           pagou costuma ser quem pagou, não quem aprova. */
                        <button
                            className="btn btn-sm btn-ghost gap-1 text-info"
                            onClick={onMarkExternal}
                            disabled={marking}
                            title="Registra o pagamento feito em outro banco e tira a conta da fila, sem transferir nada"
                        >
                            <MdAccountBalance />
                            Já foi transferida
                        </button>
                    )}

                    {/* Excluir vem ANTES de rejeitar/aprovar de propósito: é a saída de
                        quem errou o pedido, não uma decisão sobre ele. Aparece para o
                        solicitante mesmo sem poder aprovar — quem agrupou é justamente
                        quem percebe o erro. */}
                    {onDelete && (
                        <button
                            className="btn btn-sm btn-ghost text-error"
                            onClick={onDelete}
                            title={
                                item.isPayoutBatch
                                    ? 'Excluir a solicitação: os repasses voltam ao pool e a conta agrupada é apagada'
                                    : 'Excluir da fila sem decidir (a conta a pagar é mantida)'
                            }
                        >
                            <MdDelete /> Excluir
                        </button>
                    )}

                    {canApprove ? (
                        <>
                            <button
                                className="btn btn-sm btn-error btn-outline"
                                onClick={onReject}
                            >
                                <MdClose /> Rejeitar
                            </button>
                            <button
                                className="btn btn-sm btn-success"
                                onClick={onApprove}
                            >
                                <MdCheck /> Aprovar
                            </button>
                        </>
                    ) : (
                        <span className="text-xs text-base-content/50">
                            Somente operadores financeiros aprovam
                        </span>
                    )}
                </>
            }
        />
    )
}
